/**
 * O CONJUNTO DE ADESIVOS DE UMA MÁQUINA E A FOLHA QUE VAI À GRÁFICA.
 *
 * O modelo do dono (31/10/2026): uma tira de 300 × 100 mm por máquina, com o
 * adesivo PRINCIPAL (fundo branco, borda preta grossa, o código grande e um
 * QR no canto de baixo à direita) e, à direita dele, uma coluna de três
 * adesivos QUADRADOS só de QR, *"que vamos usar para colar em outros pontos
 * do equipamento"*. Preto e branco, sem marca, sem rodapé: só o código e os
 * QR.
 *
 * ## As proporções, e de onde vem cada número
 *
 * Medidas do desenho que o dono mandou, e tudo escala com a altura:
 *
 * - o quadrado tem 1/3 da altura menos os dois vãos: `(A − 2e) / 3`, a
 *   coluna inteira tem a altura da tira (31,3 mm em 100 mm);
 * - o vão entre peças é o mesmo da folha, 3 mm: cada quadrado é uma peça
 *   cortada à parte, com marca de corte própria, e a marca precisa de vão;
 * - o principal fica com o resto da largura (88,6% em 300 mm; o desenho
 *   mede 89%);
 * - a borda é 2,5% da altura (2,5 mm em 100 mm), a margem do texto, 7%;
 * - o QR do canto tem o lado do quadrado, encostado na borda do principal,
 *   com uma moldura fina (metade da borda) por dentro.
 *
 * ## O QR pequeno não pode passar de 20 mm para baixo
 *
 * Abaixo disso o celular precisa encostar no adesivo para ler, e na máquina
 * suja e no alto isso não acontece. A medida que o derruba é a ALTURA (o
 * quadrado sai dela), e a frase diz a altura mínima.
 *
 * Funções puras, em milímetro; o PDF converte para ponto no fim.
 */

/** O vão entre duas peças cortadas, na folha e dentro do conjunto. */
export const VAO_MM = 3;
/** O menor lado de QR pequeno que se aceita. */
export const MENOR_QR_MM = 20;
/** O tamanho do pedido do dono para esta versão. */
export const MEDIDA_PADRAO: MedidaDoConjunto = { largura: 300, altura: 100, extras: 3 };

const BORDA = 0.025;
const MARGEM_DO_TEXTO = 0.07;
/** A letra não passa da metade da altura: código curto ("A1") não vira um borrão. */
const LETRA_MAIOR = 0.5;

export interface MedidaDoConjunto {
  readonly largura: number;
  readonly altura: number;
  /** QR quadrados à parte, de 0 a 3. */
  readonly extras: number;
}

/** Um retângulo em mm, a partir do canto de CIMA à esquerda do conjunto. */
export interface Retangulo {
  readonly x: number;
  readonly y: number;
  readonly largura: number;
  readonly altura: number;
}

export interface DesenhoDoConjunto {
  readonly principal: Retangulo;
  readonly quadrados: readonly Retangulo[];
  /** A caixa do QR no canto do principal (dentro dele, não é peça). */
  readonly cantoDoQr: Retangulo;
  /** Onde o código pode ser escrito, já sem a borda, a margem e o QR do canto. */
  readonly areaDoTexto: Retangulo;
  readonly borda: number;
  readonly moldura: number;
  /** O lado do QR desenhado (com a zona de silêncio), igual nas quatro peças. */
  readonly ladoDoQr: number;
  readonly ladoDoQuadrado: number;
}

const arred = (n: number) => Math.round(n * 10) / 10;

/** Por que a medida não serve, dizendo o que mudar; `null` quando serve. */
export function erroDaMedida(m: MedidaDoConjunto): string | null {
  if (!Number.isFinite(m.largura) || !Number.isFinite(m.altura) || m.largura <= 0 || m.altura <= 0) {
    return 'Informe a largura e a altura do adesivo em milímetros.';
  }
  if (!Number.isInteger(m.extras) || m.extras < 0 || m.extras > 3) return 'Escolha de 0 a 3 QR à parte.';
  if (m.largura > 1000 || m.altura > 500) return 'O adesivo vai até 1000 × 500 mm. Diminua a medida.';
  const quadrado = (m.altura - 2 * VAO_MM) / 3;
  if (quadrado < MENOR_QR_MM) {
    const minima = Math.ceil(MENOR_QR_MM * 3 + 2 * VAO_MM);
    return `Com ${formatarMm(m.altura)} mm de altura cada QR pequeno fica com ${formatarMm(arred(Math.max(quadrado, 0)))} mm, e abaixo de ${String(MENOR_QR_MM)} mm o celular não lê de longe. Aumente a altura para pelo menos ${String(minima)} mm.`;
  }
  const minimaLargura = larguraMinima(m.altura, m.extras);
  if (m.largura < minimaLargura) {
    return `Com ${formatarMm(m.altura)} mm de altura o código não cabe ao lado do QR em ${formatarMm(m.largura)} mm. Aumente a largura para pelo menos ${String(Math.ceil(minimaLargura))} mm ou diminua a altura.`;
  }
  return null;
}

/** A largura em que o texto ainda tem ao menos o lado de um QR. */
function larguraMinima(altura: number, extras: number): number {
  const quadrado = (altura - 2 * VAO_MM) / 3;
  const principal = 2 * quadrado + altura * BORDA + 2 * altura * MARGEM_DO_TEXTO;
  return extras > 0 ? principal + VAO_MM + quadrado : principal;
}

/** O conjunto de uma máquina, peça a peça. Só para medida que passou em `erroDaMedida`. */
export function desenharConjunto(m: MedidaDoConjunto): DesenhoDoConjunto {
  const A = m.altura;
  const quadrado = (A - 2 * VAO_MM) / 3;
  const borda = A * BORDA;
  const moldura = borda / 2;
  const margem = A * MARGEM_DO_TEXTO;
  const larguraDoPrincipal = m.extras > 0 ? m.largura - quadrado - VAO_MM : m.largura;
  const principal = { x: 0, y: 0, largura: larguraDoPrincipal, altura: A };
  const quadrados = Array.from({ length: m.extras }, (_, i) => ({
    x: m.largura - quadrado,
    y: i * (quadrado + VAO_MM),
    largura: quadrado,
    altura: quadrado,
  }));
  const cantoDoQr = { x: larguraDoPrincipal - quadrado, y: A - quadrado, largura: quadrado, altura: quadrado };
  const areaDoTexto = {
    x: borda + margem,
    y: borda + margem,
    largura: cantoDoQr.x - margem - (borda + margem),
    altura: A - 2 * (borda + margem),
  };
  return { principal, quadrados, cantoDoQr, areaDoTexto, borda, moldura, ladoDoQr: quadrado - 2 * borda, ladoDoQuadrado: quadrado };
}

/** Onde o QR fica dentro de uma caixa: centrado no branco que sobra da borda e da moldura. */
export function qrNaCaixa(caixa: Retangulo, d: DesenhoDoConjunto, noCanto: boolean): Retangulo {
  /* No quadrado a borda é a mesma nos quatro lados. No canto, a borda do
     principal fica à direita e embaixo, e a moldura fina à esquerda e em
     cima: o branco que sobra é deslocado para lá. */
  const esquerda = noCanto ? d.moldura : d.borda;
  const direita = d.borda;
  const branco = caixa.largura - esquerda - direita;
  const folga = (branco - d.ladoDoQr) / 2;
  return { x: caixa.x + esquerda + folga, y: caixa.y + esquerda + folga, largura: d.ladoDoQr, altura: d.ladoDoQr };
}

/* ------------------------------------------------------------- o código */

/** A largura do texto num tamanho de letra, nas mesmas unidades do tamanho. */
export type MedirTexto = (texto: string, tamanho: number) => number;

export interface CodigoAjustado {
  /** O tamanho da letra (o corpo, em mm). */
  readonly tamanho: number;
  /** Onde a linha de base começa, a partir do canto de cima do conjunto. */
  readonly x: number;
  readonly linhaDeBase: number;
  readonly largura: number;
  readonly alturaDaMaiuscula: number;
}

/**
 * O MAIOR CÓDIGO QUE CABE: a largura medida com a métrica da fonte que vai no
 * PDF, a altura da maiúscula da mesma fonte, e o menor dos dois limites. O
 * código fica centrado na área do texto, que já desconta o QR do canto: ele
 * nunca encosta no QR, por mais comprido que seja.
 */
export function ajustarCodigo(codigo: string, d: DesenhoDoConjunto, medir: MedirTexto, maiusculaPorCorpo: number): CodigoAjustado {
  const larguraEmUm = medir(codigo, 1);
  const pelaLargura = larguraEmUm > 0 ? d.areaDoTexto.largura / larguraEmUm : Number.POSITIVE_INFINITY;
  const alturaMaxima = Math.min(d.areaDoTexto.altura, d.principal.altura * LETRA_MAIOR);
  const pelaAltura = alturaMaxima / maiusculaPorCorpo;
  const tamanho = Math.min(pelaLargura, pelaAltura);
  const largura = larguraEmUm * tamanho;
  const alturaDaMaiuscula = maiusculaPorCorpo * tamanho;
  return {
    tamanho,
    largura,
    alturaDaMaiuscula,
    x: d.areaDoTexto.x + (d.areaDoTexto.largura - largura) / 2,
    linhaDeBase: d.principal.altura / 2 + alturaDaMaiuscula / 2,
  };
}

/* ------------------------------------------------------------- a folha */

export interface Folha {
  readonly id: 'a4' | 'a3' | 'sra3' | 'personalizada';
  readonly nome: string;
  /** O lado menor e o maior, em mm (a folha em pé). */
  readonly largura: number;
  readonly altura: number;
}

/** As folhas de gráfica, da menor para a maior. */
export const FOLHAS_PADRAO: readonly Folha[] = [
  { id: 'a4', nome: 'A4', largura: 210, altura: 297 },
  { id: 'a3', nome: 'A3', largura: 297, altura: 420 },
  { id: 'sra3', nome: 'SRA3', largura: 320, altura: 450 },
];

/** A margem da folha: as marcas de fora vão nela. */
export const MARGEM_DA_FOLHA_MM = 10;
/** Embaixo cabe também o rodapé da folha, fora da área de corte. */
export const MARGEM_DO_PE_MM = 16;

export interface Imposicao {
  readonly porFolha: number;
  readonly colunas: number;
  readonly linhas: number;
  /** A folha deitada (paisagem). */
  readonly deitada: boolean;
  /** A folha como fica na impressão, em mm. */
  readonly largura: number;
  readonly altura: number;
  /** O canto de cima à esquerda de cada conjunto na folha, em mm. */
  readonly posicoes: readonly { readonly x: number; readonly y: number }[];
}

function naOrientacao(m: MedidaDoConjunto, largura: number, altura: number, deitada: boolean): Imposicao {
  const colunas = Math.max(0, Math.floor((largura - 2 * MARGEM_DA_FOLHA_MM + VAO_MM) / (m.largura + VAO_MM)));
  const linhas = Math.max(0, Math.floor((altura - MARGEM_DA_FOLHA_MM - MARGEM_DO_PE_MM + VAO_MM) / (m.altura + VAO_MM)));
  const larguraDaGrade = colunas * m.largura + Math.max(colunas - 1, 0) * VAO_MM;
  const alturaDaGrade = linhas * m.altura + Math.max(linhas - 1, 0) * VAO_MM;
  /* A grade fica centrada no que sobra da margem: a sobra se divide nos dois
     lados, e a guilhotina trabalha com as duas pontas iguais. */
  const x0 = (largura - larguraDaGrade) / 2;
  const y0 = MARGEM_DA_FOLHA_MM + (altura - MARGEM_DA_FOLHA_MM - MARGEM_DO_PE_MM - alturaDaGrade) / 2;
  const posicoes: { x: number; y: number }[] = [];
  for (let l = 0; l < linhas; l += 1) {
    for (let c = 0; c < colunas; c += 1) posicoes.push({ x: x0 + c * (m.largura + VAO_MM), y: y0 + l * (m.altura + VAO_MM) });
  }
  return { porFolha: colunas * linhas, colunas, linhas, deitada, largura, altura, posicoes };
}

/** Quantos conjuntos cabem numa folha: em pé e deitada, a que couber mais (empate: deitada, a tira é larga). */
export function imporNaFolha(m: MedidaDoConjunto, folha: Pick<Folha, 'largura' | 'altura'>): Imposicao {
  const menor = Math.min(folha.largura, folha.altura);
  const maior = Math.max(folha.largura, folha.altura);
  const emPe = naOrientacao(m, menor, maior, false);
  const deitada = naOrientacao(m, maior, menor, true);
  return deitada.porFolha >= emPe.porFolha ? deitada : emPe;
}

/** A menor folha padrão em que o conjunto cabe; `null` quando nem a maior serve. */
export function menorFolhaQueCabe(m: MedidaDoConjunto): Folha | null {
  return FOLHAS_PADRAO.find((f) => imporNaFolha(m, f).porFolha > 0) ?? null;
}

/** Por que o conjunto não cabe na folha, e qual serve; `null` quando cabe. */
export function erroDaFolha(m: MedidaDoConjunto, folha: Folha): string | null {
  if (!Number.isFinite(folha.largura) || !Number.isFinite(folha.altura) || folha.largura < 50 || folha.altura < 50) {
    return 'Informe a folha em milímetros, a partir de 50 × 50 mm.';
  }
  if (folha.largura > 2000 || folha.altura > 2000) return 'A folha vai até 2000 × 2000 mm. Diminua a medida.';
  if (imporNaFolha(m, folha).porFolha > 0) return null;
  const medida = `O conjunto de ${formatarMm(m.largura)} × ${formatarMm(m.altura)} mm não cabe na ${folha.nome}, nem deitada`;
  const serve = menorFolhaQueCabe(m);
  if (serve === null) return `${medida}, nem na ${FOLHAS_PADRAO.at(-1)?.nome ?? ''}. Use uma folha personalizada maior ou diminua o adesivo.`;
  return `${medida}. A menor folha padrão em que ele cabe é a ${serve.nome}, com ${String(imporNaFolha(m, serve).porFolha)} por folha.`;
}

/** "12 conjuntos · 2 por folha · 6 folhas de A3". */
export function resumoDoArquivo(conjuntos: number, porFolha: number, folha: Pick<Folha, 'nome'>): string {
  const folhas = porFolha > 0 ? Math.ceil(conjuntos / porFolha) : 0;
  return [
    `${String(conjuntos)} ${conjuntos === 1 ? 'conjunto' : 'conjuntos'}`,
    `${String(porFolha)} por folha`,
    `${String(folhas)} ${folhas === 1 ? 'folha' : 'folhas'} de ${folha.nome}`,
  ].join(' · ');
}

/** Milímetro no formato da tela (p.61): vírgula decimal, uma casa só quando há. */
export function formatarMm(n: number): string {
  const r = arred(n);
  return Number.isInteger(r) ? String(r) : r.toFixed(1).replace('.', ',');
}

/* ------------------------------------------------------------- as marcas */

export interface Marca {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/** A marca de dentro (no vão de 3 mm) e a de fora (na margem). */
const MARCA_DE_DENTRO = { afastamento: 0.5, comprimento: 2 } as const;
const MARCA_DE_FORA = { afastamento: 1, comprimento: 5 } as const;

/**
 * AS MARCAS DE CORTE de uma peça: duas em cada canto, prolongando as bordas
 * para FORA dela, sem tocar a peça. No vão de 3 mm entre peças a marca tem 2
 * mm e começa a 0,5 mm do canto, para não encostar na vizinha; na margem da
 * folha ela tem 5 mm. `grade` é o retângulo de todas as peças da folha: o
 * lado da peça que coincide com ele dá para a margem.
 */
export function marcasDaPeca(p: Retangulo, grade: Retangulo): readonly Marca[] {
  const esquerda = p.x;
  const direita = p.x + p.largura;
  const cima = p.y;
  const baixo = p.y + p.altura;
  const fora = (lado: 'e' | 'd' | 'c' | 'b') => {
    const eps = 0.001;
    if (lado === 'e') return Math.abs(esquerda - grade.x) < eps;
    if (lado === 'd') return Math.abs(direita - (grade.x + grade.largura)) < eps;
    if (lado === 'c') return Math.abs(cima - grade.y) < eps;
    return Math.abs(baixo - (grade.y + grade.altura)) < eps;
  };
  const tam = (lado: 'e' | 'd' | 'c' | 'b') => (fora(lado) ? MARCA_DE_FORA : MARCA_DE_DENTRO);
  const marcas: Marca[] = [];
  for (const y of [cima, baixo]) {
    const e = tam('e');
    const d = tam('d');
    marcas.push({ x1: esquerda - e.afastamento, y1: y, x2: esquerda - e.afastamento - e.comprimento, y2: y });
    marcas.push({ x1: direita + d.afastamento, y1: y, x2: direita + d.afastamento + d.comprimento, y2: y });
  }
  for (const x of [esquerda, direita]) {
    const c = tam('c');
    const b = tam('b');
    marcas.push({ x1: x, y1: cima - c.afastamento, x2: x, y2: cima - c.afastamento - c.comprimento });
    marcas.push({ x1: x, y1: baixo + b.afastamento, x2: x, y2: baixo + b.afastamento + b.comprimento });
  }
  return marcas;
}

/** As peças de cada conjunto da folha, já na posição da folha. */
export function pecasDaFolha(d: DesenhoDoConjunto, posicoes: readonly { readonly x: number; readonly y: number }[]): readonly Retangulo[] {
  return posicoes.flatMap((p) =>
    [d.principal, ...d.quadrados].map((r) => ({ x: p.x + r.x, y: p.y + r.y, largura: r.largura, altura: r.altura })),
  );
}

/** O retângulo que envolve as peças. */
export function envoltorio(pecas: readonly Retangulo[]): Retangulo {
  const x = Math.min(...pecas.map((p) => p.x));
  const y = Math.min(...pecas.map((p) => p.y));
  const x2 = Math.max(...pecas.map((p) => p.x + p.largura));
  const y2 = Math.max(...pecas.map((p) => p.y + p.altura));
  return { x, y, largura: x2 - x, altura: y2 - y };
}
